import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Chains, ExchangeToken, FormModel } from "../components/types";
import settings from "../settings.json";
import { appType } from "../components/constants";

export const useRates = () => {
    return useMutation({
        mutationKey: ["rate"],
        mutationFn: async (params: { fromAmount: string, toChain: Chains, requestId: number, token: ExchangeToken }) => {
            const { data, statusText, status } = await axios.get<{
                amount: string,
            }>(`${settings.server[appType]}/rate`, {
                params,
            });
            if (status >= 400) {
                throw statusText;
            }
            return { requestId: params.requestId, amount: BigInt(data.amount) };
        },
    });
};

export const useShortUrl = (fullUrl: string) => {
    return useQuery({
        queryKey: [`full-url-${fullUrl}`],
        queryFn: async () => {
            const {
                data,
                status,
                statusText,
            } = await axios.post<{ short: string }>(`${settings.server[appType]}/create-url`, {
                url: fullUrl,
            });
            if (status >= 400) {
                throw statusText;
            }
            return `${settings.server[appType]}/get-url/${data.short}`;
        }
    });
};

export const useAvailable = (token: ExchangeToken) => {
    return useQuery({
        queryKey: [`available-${token}`],
        queryFn: async () => {
            const { data, statusText, status } = await axios.get<{
                ll: string;
                eth: string;
                sol: string;
                trx: string;
            }>(`${settings.server[appType]}/available`, {
                params: { token },
            });
            if (status >= 400) {
                throw statusText;
            }
            return Object.entries(data).reduce((acc, [key, value]) => {
                acc[key as keyof typeof acc] = BigInt(value);
                return acc;
            }, {} as {
                ll: bigint;
                eth: bigint;
                sol: bigint;
                trx: bigint;
            });
        },
    })
};

export const useExchange = () => {
    return useMutation({
        mutationKey: ["exchange"],
        mutationFn: async (formModel: FormModel) => {
            const { data, statusText, status } = await axios.post<{
                executed: boolean,
            }>(`${settings.server[appType]}/exchange`, formModel);
            if (status >= 400) {
                throw statusText;
            }
            return data.executed;
        },
    });
};

export const useCreateExchange = () => {
    return useMutation({
        mutationKey: ["create-exchange"],
        mutationFn: async (formModel: FormModel) => {
            const { data, statusText, status } = await axios.post<FormModel>(
                `${settings.server[appType]}/create-exchange`,
                formModel
            );
            if (status >= 400) {
                throw statusText;
            }
            return data;
        },
    });
};